import React, { useState, useEffect } from "react";
import { sendParentOtp, verifyParentOtp } from "../api/parentApi";
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LockIcon from "@mui/icons-material/Lock";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

const ParentLoginDialog = ({ open, handleClose }) => {
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((p) => p - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    if (!open) resetState();
  }, [open]);

  const resetState = () => {
    setIdentifier("");
    setOtp("");
    setOtpSent(false);
    setTimer(0);
    setLoading(false);
    setError("");
  };

  const handleIdentifierChange = (e) => {
    setIdentifier(e.target.value);
    if (otpSent) { setOtp(""); setOtpSent(false); setTimer(0); }
    setError("");
  };

  const handleSendOtp = async () => {
    if (!identifier) { setError("Please enter your mobile number or admission number"); return; }
    setLoading(true);
    setError("");
    try {
      const payload = identifier.match(/^\d+$/)
        ? { mobileNo: identifier }
        : { applicationId: identifier };
      await sendParentOtp(payload);
      setOtpSent(true);
      setTimer(30);
    } catch (err) {
      setError(err.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) { setError("Please enter the OTP"); return; }
    setLoading(true);
    setError("");
    try {
      const payload =
        identifier.length === 10
          ? { mobileNo: identifier, applicationId: null }
          : { mobileNo: null, applicationId: identifier };

      const result = await verifyParentOtp({ ...payload, password: otp, deviceToken: "deviceToken" });

      if (result?.meta?.success) {
        const parentData = result.response;
        localStorage.setItem("parentToken", parentData.token);
        localStorage.setItem("parentInfo", JSON.stringify({
          name: parentData.GurardianName,
          email: parentData.GurardianEmailId,
          planName: parentData.planName || "Free Plan",
          planPrice: parentData.planPrice || 0,
        }));
        handleClose();
        setTimeout(() => window.location.reload(), 300);
      } else {
        setError(result?.meta?.successMessage || "Verification failed. Please try again.");
      }
    } catch (err) {
      setError("OTP verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      bgcolor: "#F8FAFF",
      "& fieldset": { borderColor: "rgba(13,27,75,0.1)", borderWidth: "1.5px" },
      "&:hover fieldset": { borderColor: "rgba(79,110,247,0.4)" },
      "&.Mui-focused fieldset": { borderColor: "#4F6EF7", borderWidth: "2px" },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#4F6EF7" },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "24px",
          overflow: "hidden",
          boxShadow: "0 40px 120px rgba(13,27,75,0.2)",
          border: "1px solid rgba(13,27,75,0.07)",
        },
      }}
    >
      {/* Header gradient strip */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0D1B4B 0%, #1A2F6E 100%)",
          px: 4,
          pt: 4,
          pb: 3,
          position: "relative",
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: "rgba(255,255,255,0.5)",
            bgcolor: "rgba(255,255,255,0.08)",
            borderRadius: "10px",
            width: 32,
            height: 32,
            "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.15)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: "#a5b4fc" }} />
          <Typography fontSize="0.78rem" fontWeight={600} color="#a5b4fc" letterSpacing="0.06em">
            PARENT PORTAL
          </Typography>
        </Box>

        <Typography variant="h5" fontWeight={800} color="#fff" mb={0.5}>
          Welcome Back
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)" }}>
          Login with your registered mobile or admission number.
        </Typography>
      </Box>

      <DialogContent sx={{ p: 4 }}>
        {/* Error */}
        {error && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: "12px",
              bgcolor: "rgba(239,68,68,0.07)",
              border: "1px solid rgba(239,68,68,0.2)",
            }}
          >
            <Typography variant="body2" color="#ef4444" fontWeight={500}>
              {error}
            </Typography>
          </Box>
        )}

        {/* Identifier field */}
        <TextField
          fullWidth
          label="Mobile or Admission number"
          value={identifier}
          onChange={handleIdentifierChange}
          disabled={otpSent && loading}
          sx={{ ...fieldSx, mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PhoneAndroidIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="#94a3b8" display="block" mb={3}>
          Example: 9876543210 or ADM10234
        </Typography>

        {/* Send OTP */}
        {!otpSent && (
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSendOtp}
            disabled={loading}
            sx={{
              py: 1.6,
              borderRadius: "50px",
              fontWeight: 700,
              fontSize: "1rem",
              background: "linear-gradient(135deg, #1A2F6E 0%, #4F6EF7 100%)",
              boxShadow: "0 10px 28px rgba(79,110,247,0.35)",
            }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : "Send OTP"}
          </Button>
        )}

        {/* OTP section */}
        {otpSent && (
          <>
            <TextField
              fullWidth
              label="Enter OTP"
              value={otp}
              onChange={(e) => { setOtp(e.target.value); setError(""); }}
              sx={{ ...fieldSx, mb: 3 }}
              inputProps={{ maxLength: 6 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleVerifyOtp}
              disabled={loading}
              sx={{
                py: 1.6,
                borderRadius: "50px",
                fontWeight: 700,
                fontSize: "1rem",
                background: "linear-gradient(135deg, #1A2F6E 0%, #4F6EF7 100%)",
                boxShadow: "0 10px 28px rgba(79,110,247,0.35)",
                mb: 2.5,
              }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : "Verify OTP"}
            </Button>

            <Box textAlign="center">
              {timer > 0 ? (
                <Typography variant="caption" color="#94a3b8">
                  Resend OTP in{" "}
                  <Box component="span" fontWeight={700} color="#4F6EF7">
                    {timer}s
                  </Box>
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    cursor: "pointer",
                    fontWeight: 600,
                    color: "#4F6EF7",
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={handleSendOtp}
                >
                  Resend OTP
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ParentLoginDialog;
